import { useState, useRef } from 'react'
import { 
  Upload, 
  FileSpreadsheet, 
  Filter, 
  X, 
  Check,
  ChevronDown,
  Eye,
  Send,
  Database,
  RefreshCw,
  MapPin,
  Settings,
  Download,
  AlertCircle
} from 'lucide-react'
import { useAppStore } from '../../state/useAppStore'

const LeftPanel = () => {
  const [activeTab, setActiveTab] = useState('routes')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)
  
  const { 
    processXLSXData,
    filterDataByDivision,
    sendToMap,
    selectedDivision,
    setSelectedDivision,
    selectedColumns,
    setSelectedColumns,
    availableColumns,
    dataPreview,
    routesData,
    invoicesData,
    activationsData,
    uploadProgress,
    isUploading,
    uploadError,
    mappingData
  } = useAppStore()

  const dataTypes = [
    { 
      id: 'routes', 
      label: 'Rute Agenti', 
      icon: FileSpreadsheet, 
      color: 'monster-cyan',
      count: routesData?.length || 0
    },
    { 
      id: 'invoices', 
      label: 'Facturi', 
      icon: Database, 
      color: 'monster-magenta',
      count: invoicesData?.length || 0
    },
    { 
      id: 'activations', 
      label: 'Activari', 
      icon: Send, 
      color: 'monster-turquoise',
      count: activationsData?.length || 0
    }
  ]

  const divisions = ['All', 'RO01', 'RO02', 'RO03', 'RO04', 'RO05', 'RO06']

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  const handleFileUpload = (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload only Excel files (.xlsx or .xls)')
      return
    }
    
    processXLSXData(file, activeTab)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    
    const file = event.dataTransfer.files[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleColumnToggle = (column) => {
    if (selectedColumns.includes(column)) {
      setSelectedColumns(selectedColumns.filter(c => c !== column))
    } else {
      setSelectedColumns([...selectedColumns, column])
    }
  }

  const toggleAllColumns = () => {
    if (selectedColumns.length === availableColumns.length) {
      setSelectedColumns([])
    } else {
      setSelectedColumns([...availableColumns])
    }
  }

  const getActiveDataCount = () => {
    switch (activeTab) {
      case 'routes': return routesData?.length || 0
      case 'invoices': return invoicesData?.length || 0
      case 'activations': return activationsData?.length || 0
      default: return 0
    }
  }

  return (
    <div className="h-full flex flex-col gap-6 overflow-y-auto pb-4">
      {/* Header */}
      <div className="monster-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="monster-title text-xl font-bold">DATA ENGINES</h2>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-gray-400">Ready</span>
          </div>
        </div>
        <p className="text-sm text-gray-300 mb-6">
          Upload and analyze commercial data streams. All engines can run independently or combined.
        </p>
        
        {/* Engine Status */}
        <div className="flex gap-3">
          {dataTypes.map(type => (
            <div key={type.id} className="flex-1 text-center">
              <div className={`text-xs px-2 py-1 rounded-full ${
                type.count > 0 
                  ? `bg-${type.color}/20 text-${type.color}` 
                  : 'bg-monster-darker text-gray-400'
              }`}>
                {type.count} {type.count === 1 ? 'record' : 'records'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Type Tabs */}
      <div className="monster-card p-4">
        <div className="flex gap-2 mb-6">
          {dataTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveTab(type.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all duration-300 ${
                activeTab === type.id 
                  ? `bg-${type.color}/20 border border-${type.color}/30` 
                  : 'hover:bg-monster-darker/30'
              }`}
            >
              <type.icon className={`w-4 h-4 ${
                activeTab === type.id ? `text-${type.color}` : 'text-gray-400'
              }`} />
              <span className={`text-sm font-orbitron ${
                activeTab === type.id ? `text-${type.color}` : 'text-gray-400'
              }`}>
                {type.label}
              </span>
              {type.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === type.id 
                    ? `bg-${type.color} text-white` 
                    : 'bg-monster-darker text-gray-400'
                }`}>
                  {type.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Upload Area */}
        <div 
          className={`border-2 ${dragOver ? 'border-monster-cyan border-solid' : 'border-dashed border-monster-border'} 
            rounded-2xl p-8 text-center mb-6 transition-all duration-300 relative
            ${dragOver ? 'bg-monster-cyan/5' : 'hover:border-monster-cyan/50'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />
          
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-monster-darker flex items-center justify-center">
            {isUploading ? (
              <RefreshCw className="w-8 h-8 text-monster-cyan animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-gray-400" />
            )}
          </div>
          
          <h3 className="text-lg font-orbitron mb-2">
            {isUploading ? 'Processing...' : 'Drop XLSX files here'}
          </h3>
          
          <p className="text-sm text-gray-400 mb-4">
            {isUploading 
              ? `Uploading... ${uploadProgress}%` 
              : 'Or click to browse (.xlsx, .xls)'}
          </p>
          
          {!isUploading && (
            <div className="monster-btn text-sm px-6 py-2 flex items-center gap-2 mx-auto inline-flex">
              <Upload className="w-4 h-4" />
              Select File
            </div>
          )}

          {isUploading && (
            <div className="mt-4">
              <div className="h-1.5 bg-monster-darker rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-monster-cyan to-monster-violet rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {uploadError && (
            <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {uploadError}
              </div>
            </div>
          )}
        </div>

        {/* Processing Options */}
        <div className="space-y-4">
          {/* Division Filter */}
          <div>
            <label className="block text-sm font-orbitron mb-2 text-gray-300">
              <Filter className="w-4 h-4 inline mr-2" />
              Filter by Division
            </label>
            <div className="flex flex-wrap gap-2">
              {divisions.map(div => (
                <button
                  key={div}
                  onClick={() => {
                    setSelectedDivision(div)
                    filterDataByDivision(div)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all duration-300 ${
                    selectedDivision === div 
                      ? 'bg-monster-cyan/20 text-monster-cyan border border-monster-cyan/30' 
                      : 'bg-monster-darker text-gray-400 hover:bg-monster-darker/70'
                  }`}
                >
                  {div}
                </button>
              ))}
            </div>
          </div>

          {/* Column Selection */}
          {availableColumns.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-orbitron text-gray-300">
                  <Eye className="w-4 h-4 inline mr-2" />
                  Data Preview Columns
                </label>
                <button
                  onClick={toggleAllColumns}
                  className="text-xs text-monster-cyan hover:text-monster-cyan/80 transition-colors"
                >
                  {selectedColumns.length === availableColumns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="bg-monster-darker rounded-xl p-4 max-h-48 overflow-y-auto">
                <div className="text-xs text-gray-400 mb-2">
                  {selectedColumns.length} of {availableColumns.length} columns selected
                </div>
                <div className="space-y-2">
                  {availableColumns.map(col => (
                    <label 
                      key={col} 
                      className="flex items-center gap-2 cursor-pointer hover:bg-monster-card/30 p-2 rounded transition-colors"
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedColumns.includes(col)}
                        onChange={() => handleColumnToggle(col)}
                        className="rounded border-monster-border bg-monster-darker text-monster-cyan focus:ring-monster-cyan"
                      />
                      <span className="text-sm truncate">{col}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Normalization Status */}
          {(routesData || invoicesData || activationsData) && (
            <div className="p-3 rounded-xl bg-monster-darker">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-monster-cyan" />
                <span className="text-sm font-orbitron">Data Processing</span>
              </div>
              <div className="space-y-1 text-xs text-gray-400">
                <div className="flex justify-between">
                  <span>Diacritics removed</span>
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <div className="flex justify-between">
                  <span>Spaces normalized</span>
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <div className="flex justify-between">
                  <span>Case standardized</span>
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <div className="flex justify-between">
                  <span>Auto-mapped to geography</span>
                  {mappingData.length > 0 ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <div className="w-3 h-3 border-2 border-monster-cyan border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Card */}
      {dataPreview.length > 0 && (
        <div className="monster-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-orbitron">DATA PREVIEW</h3>
              <p className="text-sm text-gray-400">
                Showing {dataPreview.length} of {getActiveDataCount()} records
              </p>
            </div>
            <button className="text-xs text-gray-400 hover:text-monster-cyan transition-colors flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>
          
          <div className="bg-monster-darker rounded-xl p-4 mb-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-monster-border">
                    {selectedColumns.slice(0, 4).map(col => (
                      <th key={col} className="text-left py-2 px-3 text-monster-cyan truncate">
                        {col}
                      </th>
                    ))}
                    {selectedColumns.length > 4 && (
                      <th className="text-left py-2 px-3 text-monster-cyan">
                        +{selectedColumns.length - 4} more
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dataPreview.map((row, i) => (
                    <tr key={i} className="border-b border-monster-border/30 last:border-0 hover:bg-monster-card/30">
                      {selectedColumns.slice(0, 4).map(col => (
                        <td key={col} className="py-2 px-3 truncate max-w-[150px]">
                          {row[col] || '-'}
                        </td>
                      ))}
                      {selectedColumns.length > 4 && (
                        <td className="py-2 px-3">
                          <span className="text-xs text-gray-500">View details</span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mapping Status */}
          {mappingData.length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-monster-cyan/10 to-monster-violet/10 border border-monster-border">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-monster-cyan" />
                <div className="flex-1">
                  <div className="text-sm font-orbitron">Auto-mapped to Geography</div>
                  <div className="text-xs text-gray-400">
                    {mappingData.length} locations ready for visualization
                  </div>
                </div>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={sendToMap}
              disabled={getActiveDataCount() === 0}
              className={`flex-1 monster-btn flex items-center justify-center gap-2 ${
                getActiveDataCount() === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Send className="w-4 h-4" />
              Send to Map
            </button>
            
            <button className="px-4 py-3 rounded-xl border border-monster-border hover:bg-monster-darker transition-all duration-300">
              <Download className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Data Status */}
      <div className="monster-card p-4">
        <h3 className="text-sm font-orbitron mb-3">ENGINE STATUS</h3>
        <div className="space-y-3">
          {dataTypes.map(type => {
            const isActive = activeTab === type.id
            const hasData = type.count > 0
            
            return (
              <div key={type.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    hasData ? `bg-${type.color}` : 'bg-gray-600'
                  } ${isActive ? 'animate-pulse' : ''}`} />
                  <span className="text-sm">{type.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{type.count} rec</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    hasData 
                      ? `bg-${type.color}/20 text-${type.color}` 
                      : 'bg-monster-darker text-gray-400'
                  }`}>
                    {hasData ? 'Loaded' : 'Standby'}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="monster-card p-4">
        <h3 className="text-sm font-orbitron mb-3">QUICK ACTIONS</h3>
        <div className="grid grid-cols-2 gap-2">
          <button className="p-2 rounded-xl bg-monster-darker hover:bg-monster-card transition-all duration-300 text-xs">
            Clear All Data
          </button>
          <button className="p-2 rounded-xl bg-monster-darker hover:bg-monster-card transition-all duration-300 text-xs">
            Export Preview
          </button>
          <button className="p-2 rounded-xl bg-monster-darker hover:bg-monster-card transition-all duration-300 text-xs">
            View Statistics
          </button>
          <button className="p-2 rounded-xl bg-monster-darker hover:bg-monster-card transition-all duration-300 text-xs">
            Advanced Filters
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeftPanel